import { useCallback, useEffect, useRef, useState } from 'react';
import { countryCodeFor } from '@repo/front-end-utils';

import { addressQuery, type UseAddressGeocode } from './useAddressGeocode';
import {
  formFromTwin,
  isLocated,
  looksLikePlaceholderOrigin,
  orientationError,
  orientationPatch,
  positionPatch,
  validateTwinDetails,
  type TwinDetailsForm,
  type TwinDetailsPatch,
  type TwinPosition,
} from './twinDetailsForm';

/**
 * The position controls' handlers — ONE implementation, used by Twin details and by Position on map
 * (`retire-edit-mode-position-write-path`, R4; lifted out of `OnboardingTwinDetails`, never copied).
 *
 * It owns what is typed (the pair and, where the caller tracks one, the orientation) and whether the
 * person has tried to leave the group yet. The caller owns the baseline it compares against and the
 * `commit` that queues a patch and moves that baseline, so Twin details keeps its wider record and
 * this hook reads and moves only the position part.
 *
 * - The pair saves `entered` when it is whole, valid and changed (`twin-details-page-address`, R5.3).
 * - The orientation saves alone, as the typed string, when it is valid and changed (R2).
 * - Leaving the group sends one patch holding whichever halves changed and are valid, so they share
 *   a queue patch; a half that is not valid is held with its field error.
 * - *Find* saves the address's position (`address`) only while the twin has none or sits on the old
 *   placeholder; *Reset* (Position on map, behind its confirm) saves it over a real position. Neither
 *   ever sends an orientation (R3).
 */

/** What the caller's twin holds: the pair, and the orientation where it is tracked. */
export interface PositionBaseline extends TwinPosition {
  orientation?: string;
}

interface PositionEditorErrors {
  latitude?: string;
  longitude?: string;
  orientation?: string;
}

interface Options {
  /** The twin this editor is for; a match landing after it changed is dropped. */
  twinBid: string | undefined;
  saved: PositionBaseline;
  /** The address Find and Reset search with (lines and country). */
  address: TwinDetailsForm;
  geocode: UseAddressGeocode;
  /** Queue the patch for this twin and move the caller's baseline to it. */
  commit: (patch: TwinDetailsPatch) => void;
  /** Track an orientation too (Position on map only). */
  withOrientation?: boolean;
}

export interface PositionEditor {
  coords: TwinPosition;
  orientation: string;
  /** Quiet until the person has first tried to leave the group. */
  errors: PositionEditorErrors;
  onCoordChange: (field: keyof TwinPosition, value: string) => void;
  onOrientationChange: (value: string) => void;
  /** Save what changed and is valid. `false`: something is held (half a pair, out of range). */
  onCoordCommit: () => boolean;
  /** Put back the saved values (Escape). */
  onCoordRestore: () => void;
  onFind: () => Promise<void>;
  onReset: () => Promise<void>;
  /** The caller re-read its record: show it, with the errors quiet again. */
  reseed: (values: PositionBaseline) => void;
}

/** What Find and Reset search with: the address line and the country that biases it. */
const searchKey = (f: TwinDetailsForm) => `${addressQuery(f)}|${countryCodeFor(f.country) ?? ''}`;

const samePair = (a: TwinPosition, b: TwinPosition) =>
  a.latitude.trim() === b.latitude.trim() && a.longitude.trim() === b.longitude.trim();

export const usePositionEditor = ({
  twinBid,
  saved,
  address,
  geocode,
  commit,
  withOrientation = false,
}: Options): PositionEditor => {
  const [coords, setCoords] = useState<TwinPosition>({
    latitude: saved.latitude,
    longitude: saved.longitude,
  });
  const [orientation, setOrientation] = useState(saved.orientation ?? '');
  /** The errors stay quiet until the person has tried to leave the group. */
  const [touched, setTouched] = useState(false);

  // Find and Reset read these when the answer lands: each may have changed while searching.
  const savedRef = useRef(saved);
  savedRef.current = saved;
  const coordsRef = useRef(coords);
  coordsRef.current = coords;
  const twinBidRef = useRef(twinBid);
  twinBidRef.current = twinBid;
  const addressRef = useRef(address);
  addressRef.current = address;
  const mountedRef = useRef(false);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const savedOrientation = saved.orientation ?? '';

  const onCoordChange = useCallback(
    (field: keyof TwinPosition, value: string) => setCoords(prev => ({ ...prev, [field]: value })),
    [],
  );
  const onOrientationChange = setOrientation;

  const onCoordCommit = (): boolean => {
    setTouched(true);
    let held = false;
    let patch: TwinDetailsPatch = {};
    if (!samePair(coords, saved)) {
      const pair = positionPatch(coords, 'entered');
      if (pair) patch = { ...patch, ...pair };
      else held = true;
    }
    if (withOrientation && orientation.trim() !== savedOrientation.trim()) {
      const turned = orientationPatch(orientation, savedOrientation);
      if (turned) patch = { ...patch, ...turned };
      else held = true;
    }
    if (Object.keys(patch).length > 0) {
      if ('latitude' in patch) {
        setCoords({ latitude: patch.latitude ?? '', longitude: patch.longitude ?? '' });
      }
      if (patch.orientation !== undefined) setOrientation(patch.orientation);
      commit(patch);
    }
    return !held;
  };

  const onCoordRestore = () => {
    setCoords({ latitude: saved.latitude, longitude: saved.longitude });
    setOrientation(savedOrientation);
    setTouched(false);
  };

  const reseed = useCallback((values: PositionBaseline) => {
    setCoords({ latitude: values.latitude, longitude: values.longitude });
    setOrientation(values.orientation ?? '');
    setTouched(false);
  }, []);

  /** Save a match as the twin's position, with source `address` and never an orientation. */
  const saveMatch = (lat: number, lng: number) => {
    const patch = positionPatch({ latitude: String(lat), longitude: String(lng) }, 'address');
    if (!patch) return;
    setCoords({ latitude: patch.latitude ?? '', longitude: patch.longitude ?? '' });
    commit(patch);
  };

  /**
   * Look the address up and save its position, unless `stillWanted` says the answer is no longer
   * wanted when it lands. Either way the match is dropped if the step has gone or now shows another
   * twin (its last saved values are frozen or another twin's; Copilot review), or if the address or
   * country changed while searching (the match is then another place's).
   */
  const searchAndSave = async (stillWanted: () => boolean) => {
    const forTwin = twinBid;
    const searched = searchKey(address);
    const match = await geocode.search(address);
    if (!match) return;
    if (!mountedRef.current || twinBidRef.current !== forTwin) return;
    if (searchKey(addressRef.current) !== searched) return;
    if (!stillWanted()) return;
    saveMatch(match.lat, match.lng);
  };

  // R5.2, Failure States: a match landing after the position became real is discarded.
  const onFind = () =>
    searchAndSave(() => {
      const now = savedRef.current;
      return !isLocated(now) || looksLikePlaceholderOrigin(now);
    });

  /**
   * *Move* in the Reset confirm (R3). The twin already has a real position, so Find's guard does not
   * fit; instead the match is dropped once the person has chosen another position since *Move*
   * (Failure States, Overtaken): saved another one, or typed into the fields since (a value already
   * held in them at *Move*, half a pair say, does not count: Move then replaces it).
   */
  const onReset = () => {
    const savedThen = { latitude: saved.latitude, longitude: saved.longitude };
    const typedThen = coords;
    return searchAndSave(() => {
      if (!samePair(savedRef.current, savedThen)) return false;
      const typedNow = coordsRef.current;
      return samePair(typedNow, typedThen) || samePair(typedNow, savedRef.current);
    });
  };

  const pairErrors = touched
    ? validateTwinDetails({ ...formFromTwin(null), ...coords })
    : ({} as ReturnType<typeof validateTwinDetails>);
  const errors: PositionEditorErrors = {
    latitude: pairErrors.latitude,
    longitude: pairErrors.longitude,
    orientation:
      touched && withOrientation && orientation.trim() !== savedOrientation.trim()
        ? orientationError(orientation, savedOrientation)
        : undefined,
  };

  return {
    coords,
    orientation,
    errors,
    onCoordChange,
    onOrientationChange,
    onCoordCommit,
    onCoordRestore,
    onFind,
    onReset,
    reseed,
  };
};
